<template>
    <div class="calculator__container" v-if="financeAvailable !== false">
        <div class="calculator__blue-box">
            <h3 class="calculator__blue-box-title">Finance Calculator</h3>
            <LoadingSpinner v-if="loading"></LoadingSpinner>
            <template v-else>
                <p class="calculator__label">Choose your deposit</p>
                <input type="text" v-currency="{ currency: 'GBP', locale: 'en' }" ref="depositInput" v-model="depositInput" class="form-control" />

                <p class="calculator__label">Choose your length of contract</p>
                <div class="calculator__contract-length">
                    <template v-for="availableTerm in availableTerms">
                        <input :key="'radio' + availableTerm" class="calculator__radio" type="radio" name="term" :id="'finance_app_term_' + availableTerm" :value="availableTerm" v-model.number="term" />
                        <label :key="'label' + availableTerm" class="calculator__radio-label" :for="'finance_app_term_' + availableTerm"> {{ availableTerm }} months </label>
                    </template>
                </div>

                <p v-if="availableMileagesLength > 0" class="calculator__label">Choose an annual mileage</p>
                <select v-if="availableMileagesLength > 0" name="mileage" class="form-control" v-model="mileage">
                    <option v-for="mileage in formattedAnnualMileages" :value="mileage.value" :key="mileage.value">{{ mileage.label }} miles</option>
                </select>

                <button @click="openSendEnquiryModal" type="button" class="btn btn--full-width btn--primary calculator__btn">Send enquiry</button>
            </template>
        </div>

        <div class="calculator__calculated-payments">
            <h3 class="configurator__specification-title">Calculated Payments</h3>
            <LoadingSpinner v-if="financeLoading"></LoadingSpinner>
            <div class="calculator__finance-cards" v-else>
                <finance-quote finance-key="PCP" @apply-for-finance="openApplyForFinanceModal" />
                <finance-quote finance-key="HP" @apply-for-finance="openApplyForFinanceModal" />
            </div>
        </div>

        <ApplyForFinanceModal :open="showApplyForFinanceModal" :finance-key="applyForFinanceKey" :type="applyForFinanceType" @close-modal="showApplyForFinanceModal = false" />
        <EnquireNowModal :open="showEnquireNowModal" @close-modal="showEnquireNowModal = false" />
    </div>
</template>

<script>
import { mapState } from "vuex";
import { CurrencyDirective, getValue } from "vue-currency-input";
import NumberFormat from "../../used-vue/api/NumberFormat.js";
import FinanceQuote from "./FinanceQuote.vue";
import ApplyForFinanceModal from "./ApplyForFinanceModal.vue";
import EnquireNowModal from "./EnquireNowModal.vue";
import { customEvent } from '../../modules/tracking/AnalyticsEvents';

export default {
    props: {},
    components: {
        ApplyForFinanceModal,
        EnquireNowModal,
        FinanceQuote
    },
    directives: {
        currency: CurrencyDirective,
    },
    data() {
        this.$store.dispatch("Finance/initialize");
        return {
            depositInput: this.$store.state.Finance.deposit,
            loading: true,
            financeQuotesLoading: true,
            availableTerms: [24, 36, 48],
            availableMileages: [6000, 8000, 10000],
            hasPcp: false,
            hasHp: false,
            quotes: null,
            showEnquireNowModal: false,
            showApplyForFinanceModal: false,
            applyForFinanceKey: null,
            applyForFinanceType: null,
        };
    },
    watch: {
        depositInput(val, oldVal) {
            // Ensure the deposit is a valid number
            if (typeof val == "string") {
                if (this.$refs.depositInput) {
                    val = getValue(this.$refs.depositInput);
                } else {
                    val = parseFloat(val);
                }
            }
            if (typeof val == "undefined" || val === null) {
                val = 0;
            }
            if (val < 0) {
                val = 0;
            }
            // Only trigger an update if the value has actually changed
            const currentValue = this.$store.state["Finance/deposit"] || 0;
            if (currentValue !== val) {
                this.deposit = val;
            }
        },
    },
    computed: {
        ...mapState([
            'selectedNewVehicle',
            'selectedNewVehicleIdentifier',
            'selectedNewVehicleIdentifierType',
            'pcpVehicleRequest',
            'hpVehicleRequest'
        ]),
        ...mapState('Finance', [
            'financeLoading',
            'financeAvailable',
        ]),
        availableMileagesLength() {
            return this.availableMileages.length;
        },
        deposit: {
            get: function () {
                return this.$store.state.Finance.deposit;
            },
            set: function (newValue) {
                this.$store.commit("Finance/setFinanceDeposit", newValue);
                this.$store.dispatch('Finance/getFinance');
            },
        },
        term: {
            get: function () {
                return this.$store.state.Finance.term;
            },
            set: function (newValue) {
                this.$store.commit("Finance/setFinanceTerm", newValue);
                this.$store.dispatch('Finance/getFinance');
            },
        },
        mileage: {
            get: function () {
                return this.$store.state.Finance.mileage;
            },
            set: function (newValue) {
                this.$store.commit("Finance/setFinanceMileage", newValue);
                this.$store.dispatch('Finance/getFinance');
            },
        },
        formattedAnnualMileages() {
            const formattedAnnualMileages = [];
            this.availableMileages.forEach((annualMileage) => {
                formattedAnnualMileages.push({
                    value: annualMileage,
                    label: NumberFormat(annualMileage, 0),
                });
            });
            return formattedAnnualMileages;
        },
    },
    methods: {
        openSendEnquiryModal() {
            this.$emit("interaction");
            this.showEnquireNowModal = true;
            customEvent("Configurator Final Screen", "Send Enquiry", "Clicked");
        },
        openApplyForFinanceModal(data) {
            this.$emit("interaction");
            this.applyForFinanceKey = data.key;
            this.applyForFinanceType = data.type;
            this.showApplyForFinanceModal = true;
        },
        getFinanceDefaults() {
            return new Promise((resolve, reject) => {
                // Try to collect finance from session storage cache
                let cachedFinanceParams = sessionStorage.getItem("new.finance.params." + this.selectedNewVehicle.id);

                if (cachedFinanceParams !== null) {
                    try {
                        // If we're able to parse what's stored, resolve the promise with it
                        resolve(JSON.parse(cachedFinanceParams));
                        return;
                    } catch (error) {
                        console.error(error);
                    }
                }

                const buildToOrderVehicleData = {
                    OnTheRoadPrice: this.pcpVehicleRequest.Vehicle.CashPrice,
                    Identifier: this.selectedNewVehicleIdentifier,
                    IdentifierType: this.selectedNewVehicleIdentifierType,
                    Type: this.selectedNewVehicle.carOrLCV,
                };

                // This CW API requires us specifying if the CapShortCode is a car or a van one, but the finance call itself doesn't for some reason
                if (this.selectedNewVehicleIdentifierType == "CapShortCode") {
                    buildToOrderVehicleData.IdentifierType = this.selectedNewVehicle.carOrLCV == "Car" ? "CapCarShortCode" : "CapLcvShortCode";
                }

                // If we got this far, then we were unable to get finance params from cache
                // Build the JSON request data for this vehicle
                axios
                    .post("https://services.codeweavers.net/api/finance/defaults", {
                        Credentials: {
                            ApiKey: "ONH75GHd8x0gu6yfJ6",
                        },
                        BuildToOrderVehicle: buildToOrderVehicleData,
                    })
                    .then((response) => {
                        if (typeof response.data.Error != "undefined" && response.data.Error == true) {
                            sessionStorage.setItem("new.finance.params." + this.selectedNewVehicle.id, JSON.stringify({}));
                            reject();
                            return;
                        }
                        sessionStorage.setItem("new.finance.params." + this.selectedNewVehicle.id, JSON.stringify(response.data));
                        resolve(response.data);
                    })
                    .catch((error) => {
                        console.error(error);
                        reject(error);
                    });
            });
        },
        closest(numberToMatch, arrayToSearch) {
            let closest = null,
                closestDiff = null;
            const length = arrayToSearch.length;
            for (let index = 0; index < length; index++) {
                const currentIndexValue = arrayToSearch[index];
                if (currentIndexValue == numberToMatch) {
                    return currentIndexValue;
                }
                const diff = Math.abs(numberToMatch - currentIndexValue);
                if (closestDiff == null || diff < closestDiff) {
                    closestDiff = diff;
                    closest = currentIndexValue;
                }
            }
            return closest;
        },
    },
    created() {
        this.$store
            .dispatch("getNewVehicleById")
            .then(() => {
                this.$store.commit('Finance/setFinanceAvailable', null);
                this.getFinanceDefaults().then((availableFinanceParameters) => {
                    // Combine PCP and HP restrictions
                    if (typeof availableFinanceParameters.Products == "undefined") {
                        this.loading = false;
                        return;
                    }
                    let availableTerms = [];
                    const availableMileages = [];
                    availableFinanceParameters.Products.forEach((product) => {
                        if (product.Key == "PCP") {
                            this.hasPcp = true;
                        } else if (product.Key == "HP") {
                            this.hasHp = true;
                        } else {
                            // Only interested in PCP and HP keys
                            return;
                        }

                        if (typeof product.Term != "undefined" && typeof product.Term.Values != "undefined") {
                            product.Term.Values.forEach((term) => {
                                if (term < 37) {
                                    return;
                                }
                                if (term > 48) {
                                    return;
                                }
                                if (availableTerms.indexOf(term) == -1) {
                                    availableTerms.push(term);
                                }
                            });
                        }
                        if (typeof product.AnnualMileage != "undefined" && typeof product.AnnualMileage.Values != "undefined") {
                            product.AnnualMileage.Values.forEach((annualMileage) => {
                                if (availableMileages.indexOf(annualMileage) == -1) {
                                    availableMileages.push(annualMileage);
                                }
                            });
                        }
                    });
                    if (availableTerms.length == 0) {
                        this.$store.commit('Finance/setFinanceAvailable', false);
                        return;
                    }
                    this.$store.commit('Finance/setFinanceAvailable', true);
                    availableTerms.sort((a, b) => {
                        a = parseInt(a);
                        b = parseInt(b);
                        if (a < b) {
                            return -1;
                        }
                        if (a > b) {
                            return 1;
                        }
                        return 0;
                    });
                    // Limit to at most 4 options
                    const availableTermsLength = availableTerms.length;
                    if (availableTermsLength > 4) {
                        const smallestTerm = availableTerms[0];
                        const largestTerm = availableTerms[availableTermsLength - 1];
                        const step = Math.floor((largestTerm - smallestTerm) / 3);
                        availableTerms = [smallestTerm, smallestTerm + step, smallestTerm + 2 * step, largestTerm];
                    }

                    // Set the term to the nearest of the available options to the current term value
                    this.$store.commit("Finance/setFinanceTerm", this.closest(this.term, availableTerms));

                    availableMileages.sort((a, b) => {
                        a = parseInt(a);
                        b = parseInt(b);
                        if (a < b) {
                            return -1;
                        }
                        if (a > b) {
                            return 1;
                        }
                        return 0;
                    });
                    // Set the mileage to the nearest of the available options to the current mileage value
                    this.$store.commit("Finance/setFinanceMileage", this.closest(this.mileage, availableMileages));
                    this.availableTerms = availableTerms;
                    this.availableMileages = availableMileages;
                    this.loading = false;
                    // this.$store.dispatch('Finance/getFinance');
                });
            })
            .catch((error) => {
                this.loading = false;
                this.$store.commit('Finance/setFinanceAvailable', false);
                console.error("Error loading vehicle for finance");
                console.error(error);
            });
    },
};
</script>

<style scoped lang="scss">
.calculator {
    &__container {
        @media (min-width: 992px) {
            display: grid;
            grid-template-columns: 30% 1fr;
            column-gap: 30px;
            justify-items: stretch;
            align-items: stretch;
        }
    }

    &__finance-cards {
        @media (min-width: 992px) {
            display: grid;
            grid-template-columns: 50% 50%;
            column-gap: 30px;
        }
    }

    &__blue-box {
        padding: 20px 25px;
        background-color: #193b77;
        border-radius: 10px;
        align-self: start;

        @media (max-width: 992px) {
            margin-bottom: 15px;
        }
    }

    &__radio {
        display: none;
    }

    &__radio-label {
        background: none;
        border: 0.5px solid white;
        border-radius: 10px;
        height: 45px;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        font-family: "Encode Sans", sans-serif;
        font-style: normal;
        font-weight: 400;
        font-size: 16px;
        cursor: pointer;
        color: white;
        user-select: none;

        &:hover {
            text-decoration: underline;
        }

        .calculator__radio:checked + & {
            background: white;
            color: #193b77;

            &:hover {
                text-decoration: none;
            }
        }
    }

    &__blue-box-title {
        font-size: 16px;
        font-weight: 600;
        color: white;
    }

    &__label {
        font-size: 16px;
        color: white;
        margin-bottom: 5px;
        font-weight: 400;
    }

    &__contract-length {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 15px;
    }

    &__btn {
        margin-top: 20px;
    }
}
</style>